// src/pages/PixContasAPagarPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import "./PixContasAPagarPage.css";

import {
  BadgeDollarSign,
  Download,
  CheckCircle2,
  RefreshCw,
  Search,
  RotateCcw,
  Wallet,
  Receipt,
  TrendingUp,
  Calculator,
} from "lucide-react";

function formatBRL(valor) {
  const v = Number(valor || 0);
  return v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(data) {
  if (!data) return "";
  const d = new Date(data);
  return d.toLocaleString("pt-BR");
}

export default function PixContasAPagarPage() {
  const [dados, setDados] = useState([]);
  const [filters, setFilters] = useState({
    q: "",
    cliente: "",
    empresa: "",
    data_ini: "",
    data_fim: "",
  });
  const [carregando, setCarregando] = useState(false);
  const [marcando, setMarcando] = useState({});

  const [selected, setSelected] = useState({});
  const [marcandoLote, setMarcandoLote] = useState(false);

  const selectedIds = useMemo(
    () =>
      Object.keys(selected)
        .filter((id) => selected[id])
        .map(Number)
        .filter(Boolean),
    [selected]
  );

  const allSelected =
    dados.length > 0 &&
    dados.every((r) => !!selected[r.id]);

  const resumoSelecionado = useMemo(() => {
    const set = new Set(selectedIds);

    const registros = dados.filter((r) => set.has(Number(r.id)));

    return {
      quantidade: registros.length,
      total: registros.reduce(
        (soma, r) => soma + Number(r.pix_valor || 0),
        0
      )
    };
  }, [dados, selectedIds]);

  const buildQuery = () => {
    const params = new URLSearchParams();

    if (filters.q.trim()) params.set("q", filters.q.trim());
    if (filters.cliente.trim()) params.set("cliente", filters.cliente.trim());
    if (filters.empresa.trim()) params.set("empresa", filters.empresa.trim());
    if (filters.data_ini) params.set("data_ini", filters.data_ini);
    if (filters.data_fim) params.set("data_fim", filters.data_fim);

    return params.toString();
  };

  const carregar = async () => {
    setCarregando(true);

    try {
      const qs = buildQuery();
      const res = await axios.get(
        `/admin/pix/contas-a-pagar${qs ? `?${qs}` : ""}`
      );

      setDados(Array.isArray(res.data) ? res.data : []);
      setSelected({});
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar contas a pagar.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

    const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }

    const map = {};

    dados.forEach((r) => {
      map[r.id] = true;
    });

    setSelected(map);
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => ({
      ...prev,
      [id]: checked
    }));
  };

  const marcarPago = async (id) => {
    setMarcando((prev) => ({ ...prev, [id]: true }));

    try {
      await axios.put(`/admin/pix/operacoes/${id}/marcar-pago`);
      toast.success("Pagamento marcado.");
      carregar();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao marcar pagamento.");
    } finally {
      setMarcando((prev) => ({ ...prev, [id]: false }));
    }
  };

  const marcarSelecionadosPago = async () => {
    if (!selectedIds.length) {
      toast.info("Selecione pelo menos uma conta a pagar.");
      return;
    }

    const confirmar = window.confirm(
      `Confirma a baixa de ${selectedIds.length} pagamento(s) Pix no valor total de ${formatBRL(
        resumoSelecionado.total
      )}?`
    );

    if (!confirmar) return;

    setMarcandoLote(true);

    try {
      const res = await axios.put(
        "/admin/pix/operacoes/marcar-pago-lote",
        { ids: selectedIds }
      );

      const processados = Number(res.data?.processados || 0);
      const ignorados = Number(res.data?.ignorados || 0);

      if (ignorados > 0) {
        toast.success(
          `${processados} pagamento(s) baixado(s). ${ignorados} registro(s) ignorado(s).`
        );
      } else {
        toast.success(
          `${processados} pagamento(s) marcado(s) como pago.`
        );
      }

      await carregar();
    } catch (err) {
      console.error(err);

      toast.error(
        err?.response?.data?.error ||
          "Erro ao marcar pagamentos em lote."
      );
    } finally {
      setMarcandoLote(false);
    }
  };

  const reverterAprovacao = async (id) => {
    const confirmar = window.confirm(
      "Deseja reverter esta aprovação? A operação voltará para pendente e sairá do contas a pagar."
    );

    if (!confirmar) return;

    setMarcando((prev) => ({ ...prev, [`reverter-${id}`]: true }));

    try {
      await axios.put(`/admin/pix/operacoes/${id}/reverter-aprovacao`);
      toast.success("Aprovação revertida com sucesso.");
      carregar();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Erro ao reverter aprovação.");
    } finally {
      setMarcando((prev) => ({ ...prev, [`reverter-${id}`]: false }));
    }
  };

  const exportar = async () => {
    try {
      const ids = (dados || []).map((r) => Number(r.id)).filter(Boolean);

      if (!ids.length) {
        toast.info("Não há registros para exportar.");
        return;
      }

      const res = await axios.post(
        "/admin/pix/contas-a-pagar/exportar",
        { ids },
        { responseType: "blob" }
      );

      const contentType = res.headers?.["content-type"] || "";

      if (!contentType.includes("spreadsheetml.sheet")) {
        const text = await new Response(res.data).text();
        throw new Error(text || "Resposta inválida do servidor.");
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = "pix_contas_a_pagar.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao exportar.");
    }
  };

  const limparFiltros = () => {
    setFilters({
      q: "",
      cliente: "",
      empresa: "",
      data_ini: "",
      data_fim: "",
    });

    setTimeout(() => carregar(), 0);
  };

  const resumo = useMemo(() => {
    const registros = dados.length;
    const total = dados.reduce((s, r) => s + Number(r.pix_valor || 0), 0);
    const ticketMedio = registros > 0 ? total / registros : 0;
    const maiorPix = dados.reduce(
      (maior, r) => Math.max(maior, Number(r.pix_valor || 0)),
      0
    );

    return {
      registros,
      total,
      ticketMedio,
      maiorPix,
    };
  }, [dados]);

  return (
    <div className="pixCapAdmin-page">
      <div className="pixCapAdmin-container">
        <div className="pixCapAdmin-topbar">
          <div className="pixCapAdmin-titleBlock">
            <div className="pixCapAdmin-icon">
              <BadgeDollarSign size={18} />
            </div>

            <div>
              <span className="pixCapAdmin-kicker">Pix Premiado</span>
              <h2>Contas a pagar</h2>
              <p>
                Operações aprovadas aguardando pagamento do Pix ao cliente.
              </p>
            </div>
          </div>

          <div className="pixCapAdmin-actions">
            <button
              type="button"
              onClick={carregar}
              className="pixCapAdmin-btn secondary"
              disabled={carregando || marcandoLote}
            >
              <RefreshCw size={16} />
              Atualizar
            </button>

            <button
                type="button"
                onClick={marcarSelecionadosPago}
                className="pixCapAdmin-btn success"
                disabled={!selectedIds.length || marcandoLote}
              >
                <CheckCircle2 size={16} />

                {marcandoLote
                  ? "Processando..."
                  : `Marcar pagas (${selectedIds.length})`}
              </button>

            <button
              type="button"
              onClick={exportar}
              className="pixCapAdmin-btn primary"
              disabled={!dados.length || marcandoLote}
            >
              <Download size={16} />
              Exportar
            </button>
          </div>
        </div>

        <div className="pixCapAdmin-summary">
          <div className="pixCapAdmin-card">
            <div className="pixCapAdmin-cardIcon">
              <Receipt size={18} />
            </div>
            <span>Registros pendentes</span>
            <strong>{resumo.registros}</strong>
          </div>

          <div className="pixCapAdmin-card success">
            <div className="pixCapAdmin-cardIcon">
              <Wallet size={18} />
            </div>
            <span>Total a pagar</span>
            <strong>{formatBRL(resumo.total)}</strong>
          </div>

          <div className="pixCapAdmin-card info">
            <div className="pixCapAdmin-cardIcon">
              <Calculator size={18} />
            </div>
            <span>Ticket médio Pix</span>
            <strong>{formatBRL(resumo.ticketMedio)}</strong>
          </div>

          <div className="pixCapAdmin-card warning">
            <div className="pixCapAdmin-cardIcon">
              <TrendingUp size={18} />
            </div>
            <span>Maior Pix pendente</span>
            <strong>{formatBRL(resumo.maiorPix)}</strong>
          </div>
        </div>

        <section className="pixCapAdmin-filtersCard">
          <div className="pixCapAdmin-search">
            <Search size={16} />
            <input
              placeholder="Busca livre: pedido, nome, e-mail, empresa, CNPJ..."
              value={filters.q}
              onChange={(e) =>
                setFilters((p) => ({ ...p, q: e.target.value }))
              }
            />
          </div>

          <div className="pixCapAdmin-filtersGrid">
            <div className="pixCapAdmin-field">
              <label>Cliente</label>
              <input
                placeholder="Nome ou e-mail"
                value={filters.cliente}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, cliente: e.target.value }))
                }
              />
            </div>

            <div className="pixCapAdmin-field">
              <label>Empresa</label>
              <input
                placeholder="Razão social ou CNPJ"
                value={filters.empresa}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, empresa: e.target.value }))
                }
              />
            </div>

            <div className="pixCapAdmin-field">
              <label>Cadastro de</label>
              <input
                type="date"
                value={filters.data_ini}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, data_ini: e.target.value }))
                }
              />
            </div>

            <div className="pixCapAdmin-field">
              <label>Cadastro até</label>
              <input
                type="date"
                value={filters.data_fim}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, data_fim: e.target.value }))
                }
              />
            </div>

            <button
              type="button"
              onClick={carregar}
              className="pixCapAdmin-btn primary"
            >
              Aplicar filtros
            </button>

            <button
              type="button"
              onClick={limparFiltros}
              className="pixCapAdmin-btn secondary"
            >
              Limpar
            </button>
          </div>
        </section>

        {selectedIds.length > 0 && (
            <div className="pixCapAdmin-selectionBar">
              <div>
                <strong>
                  {resumoSelecionado.quantidade} pagamento(s) selecionado(s)
                </strong>

                <span>
                  Total selecionado: {formatBRL(resumoSelecionado.total)}
                </span>
              </div>

              <button
                type="button"
                className="pixCapAdmin-clearSelection"
                onClick={() => setSelected({})}
                disabled={marcandoLote}
              >
                Limpar seleção
              </button>
            </div>
          )}

        <section className="pixCapAdmin-tableCard">
          {carregando ? (
            <div className="pixCapAdmin-loading">Carregando contas a pagar...</div>
          ) : (
            <div className="pixCapAdmin-tableWrap">
              <table className="pixCapAdmin-table">
                <thead>
                  <tr>
                    <th className="pixCapAdmin-checkCol">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={!dados.length || marcandoLote}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Selecionar todos"
                    />
                  </th>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Empresa</th>
                    <th>Pedido</th>
                    <th>Cadastro</th>
                    <th>Data Op.</th>
                    <th>Valor</th>
                    <th>Parcelas</th>
                    <th>PIX</th>
                    <th>Chave</th>
                    <th>Aprovado</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {dados.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="pixCapAdmin-empty">
                        Nenhuma conta a pagar encontrada.
                      </td>
                    </tr>
                  ) : (
                    dados.map((r) => (
                      <tr key={r.id}>

                        <td className="pixCapAdmin-checkCol">
                          <input
                            type="checkbox"
                            checked={!!selected[r.id]}
                            disabled={marcandoLote}
                            onChange={(e) =>
                              toggleOne(r.id, e.target.checked)
                            }
                          />
                        </td>
                        <td>#{r.id}</td>

                        <td>
                          <div className="pixCapAdmin-cellMain">
                            <strong>{r.cliente_nome || "-"}</strong>
                            <span>{r.cliente_email || "-"}</span>
                          </div>
                        </td>

                        <td>
                          <div className="pixCapAdmin-cellMain">
                            <strong>{r.empresa_nome || "-"}</strong>
                            <span>{r.cliente_cnpj_empresa || ""}</span>
                          </div>
                        </td>

                        <td>{r.numero_pedido}</td>
                        <td>{formatDate(r.criado_em)}</td>
                        <td>{r.data_operacao ? String(r.data_operacao) : "-"}</td>
                        <td>{formatBRL(r.valor)}</td>
                        <td>{r.numero_parcelas}x</td>

                        <td>
                          <span className="pixCapAdmin-pixValue">
                            {formatBRL(r.pix_valor)}
                          </span>
                        </td>

                        <td>
                          <div className="pixCapAdmin-cellMain chave">
                            <strong>{r.pix_chave_tipo || "-"}</strong>
                            <span>{r.pix_chave_valor || "-"}</span>
                          </div>
                        </td>

                        <td>{formatDate(r.data_aprovacao)}</td>

                        <td>
                          <div className="pixCapAdmin-actionsCol">
                            <button
                              type="button"
                              className="pixCapAdmin-actionBtn success"
                              onClick={() => marcarPago(r.id)}
                              disabled={
                                marcando[r.id] || marcando[`reverter-${r.id}`]
                              }
                            >
                              <CheckCircle2 size={15} />
                              {marcando[r.id] ? "..." : "Marcar pago"}
                            </button>

                            <button
                              type="button"
                              className="pixCapAdmin-actionBtn secondary"
                              onClick={() => reverterAprovacao(r.id)}
                              disabled={
                                marcando[r.id] || marcando[`reverter-${r.id}`]
                              }
                              title="Reverter aprovação"
                            >
                              <RotateCcw size={15} />
                              {marcando[`reverter-${r.id}`] ? "..." : "Reverter"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}